/* Progressive presentation only. Never fetch, claim, redeem, or calculate MP. */
(() => {
  if (window.smRewardsDesign) return;
  window.smRewardsDesign = true;
  const mounted = new Map();
  const mount = (root) => {
    if (mounted.has(root)) return;
    const abort = new AbortController();
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 749px)');
    const disclosures = new Map();
    const cleanups = [];
    const enhanceDisclosure = (details) => {
      if (disclosures.has(details)) return;
      const summary = details.querySelector(':scope > summary');
      const body = details.querySelector(':scope > .sm-rewards-disclosure-body');
      const content = body?.firstElementChild;
      if (!summary || !body || !content) return;
      let expanded = details.open;
      let frame = 0, height = 0, velocity = 0, last = 0;
      const settle = () => {
        cancelAnimationFrame(frame); frame = 0;
        details.open = expanded;
        body.style.height = '';
        body.style.opacity = '';
      };
      const tick = (now) => {
        if (!details.isConnected || reduced.matches) return settle();
        const dt = Math.min((now - last) / 1000, .04); last = now;
        const target = expanded ? content.getBoundingClientRect().height : 0;
        const offset = height - target;
        const omega = 26; // Critically damped: smooth, no bounce.
        const decay = Math.exp(-omega * dt);
        const blend = velocity + omega * offset;
        height = target + (offset + blend * dt) * decay;
        velocity = (velocity - omega * blend * dt) * decay;
        body.style.height = `${Math.max(0, height)}px`;
        body.style.opacity = String(expanded ? Math.min(1, height / Math.max(1, target)) : Math.min(1, height / Math.max(1, content.getBoundingClientRect().height)));
        if (Math.abs(height - target) < .25 && Math.abs(velocity) < 2) return settle();
        frame = requestAnimationFrame(tick);
      };
      const toggle = (event) => {
        event.preventDefault();
        height = details.open ? body.getBoundingClientRect().height : 0;
        expanded = !expanded;
        details.dataset.expanded = String(expanded);
        if (reduced.matches) return settle();
        details.open = true;
        body.style.height = `${height}px`;
        cancelAnimationFrame(frame);
        last = performance.now();
        frame = requestAnimationFrame(tick);
      };
      summary.addEventListener('click', toggle);
      const resize = new ResizeObserver(() => {
        if (frame || !expanded) return;
        body.style.height = '';
      });
      resize.observe(content);
      disclosures.set(details, () => {
        cancelAnimationFrame(frame);
        resize.disconnect();
        summary.removeEventListener('click', toggle);
      });
    };
    const tierTrack = root.querySelector('[data-bb-tier-track]');
    let railFrame = 0;
    const illuminateRail = () => {
      railFrame = 0;
      if (reduced.matches || !tierTrack?.isConnected) return;
      const readingLine = innerHeight * .58;
      tierTrack.querySelectorAll(':scope > .bb-rewards-tier').forEach(card => {
        const connector = card.querySelector('.sm-rewards-tier-connector');
        if (!connector) return;
        const bounds = connector.getBoundingClientRect();
        const number = card.querySelector('.sm-rewards-tier-number')?.getBoundingClientRect();
        const active = bounds.height > 0 && readingLine >= bounds.top - 20 && readingLine <= bounds.bottom + 20;
        connector.style.setProperty('--sm-light-y', `${Math.max(-32,Math.min(bounds.height - 32,readingLine - bounds.top - 32))}px`);
        connector.style.setProperty('--sm-light-visible', active ? '1' : '0');
        card.dataset.reading = String(Boolean(number && Math.abs(readingLine - (number.top + number.height / 2)) < 48));
      });
    };
    const queueRail = () => {
      if (!railFrame && !reduced.matches) railFrame = requestAnimationFrame(illuminateRail);
    };
    window.addEventListener('scroll',queueRail,{passive:true,signal:abort.signal});
    window.addEventListener('resize',queueRail,{signal:abort.signal});
    if (tierTrack) {
      const railResize = new ResizeObserver(queueRail);
      railResize.observe(tierTrack);
      cleanups.push(() => {railResize.disconnect();cancelAnimationFrame(railFrame);});
    }
    const updateLevelStates = () => {
      const cards = [...(tierTrack?.querySelectorAll(':scope > .bb-rewards-tier') || [])];
      const currentIndex = cards.findIndex(card => card.classList.contains('is-current'));
      const verified = root.dataset.profileVerified === 'true' && currentIndex >= 0;
      const fraction = Math.max(0,Math.min(1,Number(root.querySelector('[data-bb-progress-bar]')?.getAttribute('aria-valuenow')) / 100 || 0));
      cards.forEach((card, index) => {
        const state = !verified ? 'preview' : index < currentIndex ? 'achieved' : index === currentIndex ? 'current' : 'upcoming';
        card.dataset.levelState = state;
        card.querySelector('.sm-rewards-tier-connector')?.style.setProperty('--sm-earned',state === 'achieved' ? '1' : state === 'current' ? String(fraction) : '0');
        const summary = card.querySelector('.sm-rewards-tier-summary');
        if (state === 'current') summary?.setAttribute('aria-current', 'step');
        else summary?.removeAttribute('aria-current');
        const badge = card.querySelector('.sm-rewards-tier-badge');
        if (!badge) return;
        badge.textContent = state === 'current' ? (root.dataset.currentLevelLabel || 'Your level')
          : state === 'achieved' ? (root.dataset.achievedLevelLabel || 'Achieved')
          : verified && index === currentIndex + 1 ? (root.dataset.nextLevelLabel || 'Next level') : '';
        badge.hidden = !badge.textContent;
      });
      queueRail();
    };
    const upgradeTiers = () => {
      for (const [details, cleanup] of disclosures) {
        if (!root.contains(details)) {cleanup(); disclosures.delete(details);}
      }
      tierTrack?.querySelectorAll(':scope > .bb-rewards-tier').forEach((card, index) => {
        if (card.querySelector('details')) return;
        const heading = card.querySelector(':scope > h3');
        const qualification = card.querySelector(':scope > p');
        const benefits = card.querySelector(':scope > div');
        if (!heading || !qualification || !benefits) return;
        const details = document.createElement('details');
        details.dataset.smDisclosure = '';
        const summary = document.createElement('summary');
        summary.className = 'sm-rewards-tier-summary';
        const number = document.createElement('span');
        number.className = 'sm-rewards-tier-number';
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(index + 1).padStart(2, '0');
        const copy = document.createElement('span');
        copy.className = 'sm-rewards-tier-copy';
        const level = document.createElement('span');
        level.className = 'sm-rewards-tier-level';
        level.textContent = `${root.dataset.levelLabel || 'Level'} ${String(index + 1).padStart(2, '0')}`;
        const badge = document.createElement('span');
        badge.className = 'sm-rewards-tier-badge';
        badge.hidden = true;
        copy.append(level, heading, qualification, badge);
        if (card.classList.contains('is-current')) details.open = true;
        const chevron = document.createElement('span');
        chevron.className = 'sm-rewards-chevron';
        chevron.setAttribute('aria-hidden', 'true');
        summary.append(number, copy, chevron);
        const body = document.createElement('div');
        body.className = 'sm-rewards-disclosure-body';
        benefits.className = 'sm-rewards-disclosure-content';
        body.append(benefits);
        details.append(summary, body);
        const connector = document.createElement('span');
        connector.className = 'sm-rewards-tier-connector';
        connector.setAttribute('aria-hidden', 'true');
        card.querySelector('.bb-rewards-tier__mark')?.remove();
        card.append(connector, details);
        enhanceDisclosure(details);
      });
      updateLevelStates();
    };
    root.querySelectorAll('[data-sm-disclosure]').forEach(enhanceDisclosure);
    upgradeTiers();
    if (tierTrack) {
      const observer = new MutationObserver(upgradeTiers);
      observer.observe(tierTrack, {childList:true});
      cleanups.push(() => observer.disconnect());
    }
    const progressBar = root.querySelector('[data-bb-progress-bar]');
    if (progressBar) {
      const progressObserver = new MutationObserver(updateLevelStates);
      progressObserver.observe(progressBar,{attributes:true,attributeFilter:['aria-valuenow']});
      cleanups.push(() => progressObserver.disconnect());
    }
    // Feedback confirms a new server award; it never changes an account balance.
    const effects = new Set();
    const spring = t => (1 - (1 + 8 * t) * Math.exp(-8 * t)) / (1 - 9 * Math.exp(-8));
    const celebrate = event => {
      const {points,anchor} = event.detail || {};
      if (reduced.matches || !Number.isFinite(points) || points <= 0 || !root.contains(anchor)) return;
      const host = anchor.closest('.bb-rewards-earn');
      if (!host || !anchor.animate) return;
      const source = anchor.getBoundingClientRect();
      const bounds = host.getBoundingClientRect();
      const feedback = document.createElement('span');
      feedback.className = 'sm-rewards-claim-feedback';
      feedback.setAttribute('aria-hidden','true');
      const check = document.createElement('span');
      check.className = 'sm-rewards-claim-check';
      check.textContent = '✓';
      const label = document.createElement('span');
      label.textContent = `+${points.toLocaleString(undefined,{maximumFractionDigits:0})} MP`;
      feedback.append(check,label);
      feedback.style.left = `${Math.max(0,Math.min(bounds.width - 160,source.left - bounds.left + source.width / 2 - 70))}px`;
      feedback.style.top = `${source.top - bounds.top - 46}px`;
      const halo = document.createElement('span');
      halo.className = 'sm-rewards-claim-halo';
      halo.setAttribute('aria-hidden','true');
      Object.assign(halo.style,{left:`${source.left - bounds.left}px`,top:`${source.top - bounds.top}px`,width:`${source.width}px`,height:`${source.height}px`});
      host.append(halo,feedback);
      const frames = Array.from({length:51},(_,i) => {
        const t = i / 50;
        const entry = spring(Math.min(1,t / .28));
        const exit = spring(Math.max(0,(t - .7) / .3));
        return {offset:t,opacity:Math.min(1,t / .12) * (1 - exit),transform:`translateY(${8 * (1 - entry) - 12 * exit}px) scale(${.94 + .06 * entry})`};
      });
      const duration = mobile.matches ? 850 : 1000;
      const animation = feedback.animate(frames,{duration,easing:'linear'});
      const ring = halo.animate(Array.from({length:21},(_,i) => ({offset:i / 20,opacity:1 - i / 20,transform:`scale(${1 + .16 * spring(i / 20)})`})),{duration:480,easing:'linear'});
      const balance = root.querySelector('[data-bb-balance]');
      const glint = balance?.animate([{opacity:.65},{opacity:1}],{duration:300,easing:'ease-out'});
      const cleanup = () => {
        animation.cancel();ring.cancel();glint?.cancel();feedback.remove();halo.remove();effects.delete(cleanup);
      };
      effects.add(cleanup);
      animation.finished.then(cleanup,cleanup);
    };
    root.addEventListener('sm:reward-claimed',celebrate,{signal:abort.signal});
    cleanups.push(() => [...effects].forEach(cleanup => cleanup()));
    const earnings = root.querySelector('[data-bb-earning-actions]');
    if (earnings) {
      const toggle = document.createElement('button');
      toggle.type = 'button'; toggle.className = 'bb-rewards-more';
      earnings.id ||= `${root.id}-earning-actions`;
      toggle.setAttribute('aria-controls', earnings.id);
      let expanded = false;
      const update = () => {
        const items = [...earnings.querySelectorAll(':scope > .bb-rewards-earn__item')];
        toggle.hidden = !mobile.matches || items.length <= 5;
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.textContent = expanded ? 'Show fewer ways to earn' : `Show ${Math.max(0, items.length - 5)} more`;
        items.forEach((item, i) => {item.hidden = mobile.matches && i >= 5 && !expanded;});
      };
      toggle.addEventListener('click', () => {expanded = !expanded;update();}, {signal:abort.signal});
      earnings.after(toggle);
      const observer = new MutationObserver(update);
      observer.observe(earnings, {childList:true});
      mobile.addEventListener('change', update, {signal:abort.signal});
      cleanups.push(() => {observer.disconnect(); toggle.remove();});
      update();
    }
    const settleAll = () => {
      queueRail();
      if (!reduced.matches) return;
      [...effects].forEach(cleanup => cleanup());
      cancelAnimationFrame(railFrame);railFrame = 0;
      root.querySelectorAll('[data-sm-disclosure]').forEach((details) => {
        // Commit the requested state immediately when the preference changes.
        details.open = details.dataset.expanded ? details.dataset.expanded === 'true' : details.open;
      });
    };
    reduced.addEventListener('change', settleAll, {signal:abort.signal});
    const updateBalanceLabels = () => {
      updateLevelStates();
      if (root.dataset.profileVerified !== 'true') return;
      root.querySelectorAll('[data-bb-balance][aria-label], [data-bb-tier-points][aria-label]').forEach((field) => field.removeAttribute('aria-label'));
    };
    updateBalanceLabels();
    const profileObserver = new MutationObserver(updateBalanceLabels);
    profileObserver.observe(root, {attributes:true, attributeFilter:['data-profile-verified']});
    cleanups.push(() => profileObserver.disconnect());
    mounted.set(root, () => {
      abort.abort(); cleanups.forEach((cleanup) => cleanup());
      disclosures.forEach((cleanup) => cleanup()); disclosures.clear();
      mounted.delete(root);
    });
  };
  const mountWithin = (scope) => {
    if (scope.matches?.('[data-sm-rewards-design]')) mount(scope);
    scope.querySelectorAll('[data-sm-rewards-design]').forEach(mount);
  };
  mountWithin(document);
  document.addEventListener('shopify:section:load', (event) => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, cleanup] of mounted) if (event.target === root || event.target.contains(root)) cleanup();
  });
})();
